/**
 * packNav — builds sidebar NavSections from the pack manifest's wire nav
 * projection (GET /api/v1/packs/installed → sidebarSections).
 *
 * Phase-0 pack spine: this REPLACES the deleted client-side mirror
 * (seniorLivingNav.ts). The server-side pack manifest
 * (lib/packs/<pack>.ts → sidebar_sections) is the single source of
 * truth for which items a pack contributes, their labels, and their
 * order. The client owns only presentation concerns the manifest cannot
 * ship over JSON:
 *
 *   - ICONS   — resolved here from PACK_NAV_ICONS, keyed by route (the
 *               stable wire identifier). Unknown route → fallback icon,
 *               never a dropped item.
 *   - NAV IDS — resolved via routeMapping.getNavIdForRoute so selection
 *               active-state and handleNavSelect (navigate(getNavRoute))
 *               keep working unchanged. An item whose route has NO nav
 *               id mapping cannot navigate; it is dropped with a
 *               console.warn — this is the "manifest and client drifted"
 *               diagnostic, loud in dev, harmless in prod.
 *   - BADGES  — the manifest declares badge_source (e.g. "inbox_unread");
 *               the sidebar supplies live counts per badge source and
 *               this builder attaches them (99+ cap, hidden at 0),
 *               replacing the old withMessengerBadge special-case.
 *
 * NESTING (one level). A manifest item may declare `children`; it then
 * renders as a SkyNavDrawer category (expand/collapse; the library never
 * fires onSelect for a category, so only the children navigate). Parent
 * and child may share a route — "Referrers" is both the group and its
 * person-first list — so the PARENT resolves its nav id through
 * routeMapping.getHubNavIdForRoute (the hub map) while children resolve
 * through the normal getNavIdForRoute. Without that split the reverse
 * route→id map could only answer with one id and parent + child would
 * collide on a single NavItem id (duplicate React keys, and both matching
 * `selectedId`).
 */

import {
  Gauge,
  LayoutDashboard,
  Users,
  House,
  UserPlus,
  Building2,
  LayoutGrid,
  FileChartColumn,
  MessageSquare,
  Calendar,
  ShieldCheck,
  Circle,
  Sparkles,
  BrainCircuit,
  BookOpen,
  FileText,
  Plug,
} from "lucide-react";
import type { NavItem, NavSection } from "@skypointcloud/skypoint-ui";
import type { WireSidebarItem, WireSidebarSection } from "../../../store/api/packsApi";
import type { Terminology } from "../../../../lib/packs/terminology";
import { getHubNavIdForRoute, getNavIdForRoute } from "./routeMapping";

type NavIcon = NavItem["icon"];

/**
 * Route → icon. Keyed by route because that is the wire contract; a
 * pack manifest adding an item with a route not listed here renders
 * with the fallback icon until this registry learns it.
 */
const PACK_NAV_ICONS: Record<string, NavIcon> = {
  "/crm/overview": Gauge,
  "/crm/day-planner": LayoutDashboard,
  "/crm/persons": Users,
  "/crm/residents": House,
  "/crm/referrer-persons": UserPlus,
  "/crm/organizations": Building2,
  "/crm/occupancy": LayoutGrid,
  "/crm/reports": FileChartColumn,
  "/crm/messenger": MessageSquare,
  "/crm/events": Calendar,
  "/crm/health": ShieldCheck,
  "/crm/skyagent": Sparkles,
  "/crm/skyagent/brain": BrainCircuit,
  "/crm/skyagent/skills": BookOpen,
  "/crm/skyagent/drafts": FileText,
  "/crm/skyagent/connectors": Plug,
};

const FALLBACK_ICON: NavIcon = Circle;

/**
 * Live badge counts keyed by the manifest's badge_source id, supplied
 * by the sidebar (e.g. { inbox_unread: 3 }). Counts <= 0 render no badge.
 */
export type BadgeCounts = Record<string, number>;

export function buildPackNavSections(
  sections: WireSidebarSection[],
  badgeCounts: BadgeCounts = {},
  /**
   * Resolved terminology (PackContext.terms). Items declaring a `term`
   * key render the resolved noun (pack + tenant overrides) instead of
   * their static label — nav and pages rename together.
   */
  terms?: Terminology,
  /**
   * Routes to leave out for THIS tenant, even though the manifest declares
   * them. The manifest is static per vertical and cannot know what a given
   * tenant has configured; Yardi Resolution is the case that forced this —
   * every senior-living tenant was shown a Yardi item, and one that has never
   * set Yardi up got a page that could only tell them so.
   *
   * Availability only. Permission gating stays with the route and the API —
   * hiding an item the operator lacks rights to is a nicety, and hiding one
   * they DO have rights to would be a bug.
   */
  hiddenRoutes?: ReadonlySet<string>,
): NavSection[] {
  const label = (item: WireSidebarItem) => (item.term && terms?.[item.term]) || item.label;

  const badge = (item: WireSidebarItem) => {
    const count = item.badgeSource ? (badgeCounts[item.badgeSource] ?? 0) : 0;
    return count > 0 ? { badge: count > 99 ? "99+" : count } : {};
  };

  /** A leaf (navigable) item: nav id from the route, or dropped with a warn. */
  const toLeaf = (item: WireSidebarItem): NavItem[] => {
    // Unavailable for this tenant. Silent, unlike the drift warning below:
    // this is a configuration answer, not a manifest/client mismatch.
    if (hiddenRoutes?.has(item.route)) return [];
    const navId = getNavIdForRoute(item.route);
    if (!navId) {
      // Manifest/client drift: the manifest declares a route this
      // client build cannot navigate to. Drop it loudly rather than
      // render a dead item.
      console.warn(
        `[packNav] No nav id mapping for pack sidebar route "${item.route}" (label "${item.label}") — item dropped. ` +
          `Add the route to routeMapping.ts NAV_TO_ROUTE_BASE (and ROUTE_PATTERNS) to render it.`,
      );
      return [];
    }
    return [
      {
        id: navId,
        label: label(item),
        icon: PACK_NAV_ICONS[item.route] ?? FALLBACK_ICON,
        ...badge(item),
      },
    ];
  };

  return sections.map((section) => ({
    header: section.label,
    items: section.items.flatMap<NavItem>((item) => {
      if (!item.children?.length) return toLeaf(item);

      const children = item.children.flatMap(toLeaf);
      const hubId = getHubNavIdForRoute(item.route);

      // A group repeats its own route as the first child so the parent stays
      // reachable. Once hiding leaves ONLY that self-child, there is no group
      // left to draw — rendering one would give a "Occupancy > Occupancy"
      // expander around a single item. Fall back to the flat parent.
      const selfId = getNavIdForRoute(item.route);
      if (children.length > 0 && selfId && children.every((c) => c.id === selfId)) {
        return toLeaf(item);
      }

      if (hubId && children.length > 0) {
        return [
          {
            id: hubId,
            label: label(item),
            icon: PACK_NAV_ICONS[item.route] ?? FALLBACK_ICON,
            ...badge(item),
            children,
          },
        ];
      }

      // Degrade rather than hide pages. Two distinct drifts, two answers,
      // neither of which may emit the same nav id twice (parent and child
      // legitimately share a route):
      //   - no hub id for the parent route → render the CHILDREN flat, drop
      //     the group wrapper (every page stays reachable).
      //   - hub id present but no child resolved → render the PARENT flat,
      //     i.e. exactly the pre-nesting behaviour.
      console.warn(
        `[packNav] Nested pack sidebar item "${item.label}" (route "${item.route}") could not render as a group ` +
          `(${hubId ? "no child route resolved to a nav id" : "no hub nav id for the parent route"}) — falling back to flat items. ` +
          `Add the parent route to HUB_ROUTE_TO_NAV_ID in routeMapping.ts (and each child route to NAV_TO_ROUTE_BASE / ROUTE_PATTERNS).`,
      );
      return children.length > 0 ? children : toLeaf(item);
    }),
  }));
}
